import { useAui, useAuiState } from '@assistant-ui/react';
import { useEffect, useId, useMemo, useState } from 'react';

import { Button } from '@playbooks/ui/components/button';
import { Icon } from '@playbooks/ui/components/icon';

const ComposerReply = ({ message, onClear }) => {
	if (!message) return null;
	return (
		<div className='flex-between gap-3 border-b border-border px-3 py-2 text-xs'>
			<span className='min-w-0'>
				<strong className='block truncate'>
					Replying to {message.author?.name || message.owner?.name || 'Playbooks'}
				</strong>
				<span className='block truncate text-foreground-subtle'>{message.text}</span>
			</span>
			<Button size='icon-xs' variant='ghost' aria-label='Cancel reply' onClick={onClear}>
				<Icon type='far' icon='xmark' aria-hidden />
			</Button>
		</div>
	);
};

const escapeRegExp = value => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ComposerMentions = ({ targets = {} as any, selected = [], onSelect, onRemove = undefined }) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [dismissedText, setDismissedText] = useState(null);
	const aui = useAui();
	const text = useAuiState((state: any) => state.composer.text || '');
	const match = text.match(/@([^@\n]*\S)?$/);
	const mentionActive = Boolean(match);
	const listboxId = useId();

	// Computed
	const query = (match?.[1] || '').trim().toLowerCase();
	const options = useMemo(
		() =>
			[
				...(targets.people || []).map(item => ({ ...item, targetType: 'person', userId: item.id })),
				...(targets.projects || []).map(item => ({ ...item, targetType: 'project', projectId: item.id })),
				...(targets.playbooks
					? [{ ...targets.playbooks, targetType: 'playbooks', workspaceId: targets.playbooks.id }]
					: []),
			]
				.filter(item => {
					const key =
						item.targetType === 'person' ? 'userId' : item.targetType === 'project' ? 'projectId' : 'workspaceId';
					return !selected.some(value => value.targetType === item.targetType && value[key] === item.id);
				})
				.filter(
					item =>
						!query ||
						String(item.name || '')
							.toLowerCase()
							.includes(query),
				)
				.slice(0, 6),
		[query, selected, targets.people, targets.playbooks, targets.projects],
	);
	const optionsKey = options.map(item => `${item.targetType}-${item.id}`).join('|');
	const isOpen = mentionActive && dismissedText !== text && options.length > 0;

	// Methods
	const select = item => {
		if (!match) return;
		const next = text.slice(0, match.index) + `@${item.name} `;
		aui.composer().setText(next);
		onSelect(item);
	};

	// Hooks
	useEffect(() => {
		if (!onRemove || mentionActive) return;
		selected.forEach(item => {
			if (item.name && !new RegExp(`(^|\\s)@${escapeRegExp(item.name)}(?=\\s|$)`).test(text)) onRemove(item);
		});
	}, [mentionActive, onRemove, selected, text]);

	useEffect(() => {
		setActiveIndex(0);
	}, [optionsKey, query]);

	useEffect(() => {
		if (dismissedText !== null && dismissedText !== text) setDismissedText(null);
	}, [dismissedText, text]);

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = event => {
			if (!(event.target instanceof HTMLTextAreaElement) || event.target.getAttribute('aria-label') !== 'Message agent')
				return;
			if (event.isComposing) return;
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				event.stopPropagation();
				setActiveIndex(index => (index + 1) % options.length);
			}
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				event.stopPropagation();
				setActiveIndex(index => (index - 1 + options.length) % options.length);
			}
			if (event.key === 'Enter') {
				event.preventDefault();
				event.stopPropagation();
				select(options[activeIndex]);
			}
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				setDismissedText(text);
			}
		};
		document.addEventListener('keydown', onKeyDown, true);
		return () => document.removeEventListener('keydown', onKeyDown, true);
	}, [activeIndex, isOpen, options, text]);

	if (!isOpen) return null;
	return (
		<div
			id={listboxId}
			role='listbox'
			aria-label='Mention suggestions'
			className='absolute inset-x-0 bottom-full z-flyout mb-2 max-h-60 overflow-y-auto rounded-panel border border-border bg-surface-raised p-1 shadow-flyout'>
			{options.map((item, index) => (
				<Button
					key={`${item.targetType}-${item.id}`}
					role='option'
					aria-selected={index === activeIndex}
					variant='ghost'
					size='sm'
					prevIcon={item.targetType === 'person' ? 'user' : item.targetType === 'project' ? 'cube' : 'grid-2'}
					onMouseDown={event => event.preventDefault()}
					onMouseEnter={() => setActiveIndex(index)}
					onClick={() => select(item)}
					className={
						index === activeIndex ? 'w-full justify-start bg-surface-hover text-foreground' : 'w-full justify-start'
					}>
					{item.name}
				</Button>
			))}
		</div>
	);
};

const ComposerIntentConfirmation = ({ confirmation, onChoose, onDismiss }) => {
	const aui = useAui();
	if (!confirmation) return null;
	const options = confirmation.options?.length
		? confirmation.options
		: [
				{ id: 'comment', label: 'Add comment' },
				{ id: 'prompt', label: 'Run with Playbooks' },
			];
	const choose = intent => {
		onChoose(intent);
		aui.composer().setText(confirmation.text);
		queueMicrotask(() => aui.composer().send());
	};
	return (
		<div className='mx-2 mb-2 rounded-panel border border-warning/40 bg-surface px-3 py-2'>
			<p className='text-sm font-medium'>{confirmation.question || 'Who is this message for?'}</p>
			{confirmation.options?.length ? null : (
				<p className='mt-0.5 text-xs text-foreground-body'>Add it for collaborators, or ask Playbooks to act on it.</p>
			)}
			<div className='mt-2 flex flex-wrap gap-2'>
				{options.map((option, index) => (
					<Button
						key={option.id}
						size='xs'
						variant={index === options.length - 1 ? 'default' : 'outline'}
						onClick={() => choose(option.id)}>
						{option.label}
					</Button>
				))}
				<Button size='xs' variant='ghost' onClick={onDismiss}>
					Cancel
				</Button>
			</div>
		</div>
	);
};

export { ComposerIntentConfirmation, ComposerMentions, ComposerReply };
